import React, { useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import PhoneGroupForm from "../components/PhoneGroupForm";
import { phoneGroupApi } from "../services/api";

const PhoneGroupList = () => {
	const [groups, setGroups] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [currentGroup, setCurrentGroup] = useState(null);

	const fetchGroups = async () => {
		try {
			setLoading(true);
			const response = await phoneGroupApi.list({});
			if (response.code === 1) {
				setGroups(response.data || []);
			} else {
				alert(response.msg || "获取列表失败");
			}
		} catch (error) {
			console.error("Fetch groups error:", error);
			alert("获取列表异常");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchGroups();
	}, []);

	const handleAdd = () => {
		setCurrentGroup(null);
		setIsModalOpen(true);
	};

	const handleEdit = (group) => {
		setCurrentGroup(group);
		setIsModalOpen(true);
	};

	const handleDelete = async (id) => {
		if (window.confirm("确认要删除这个电话分组吗？")) {
			try {
				const response = await phoneGroupApi.delete(id);
				if (response.code === 1) {
					fetchGroups();
				} else {
					alert(response.msg || "删除失败");
				}
			} catch (error) {
				console.error("Delete error:", error);
				alert("删除异常");
			}
		}
	};

	const handleSubmit = async (data) => {
		try {
			let response;
			if (currentGroup && currentGroup.id) {
				response = await phoneGroupApi.update(currentGroup.id, data);
			} else {
				response = await phoneGroupApi.create(data);
			}

			if (response.code === 1) {
				setIsModalOpen(false);
				fetchGroups();
			} else {
				alert(response.msg || "保存失败");
			}
		} catch (error) {
			console.error("Submit error:", error);
			alert("保存异常");
		}
	};

	return (
		<div className="min-h-screen bg-gray-50/50 p-4 sm:p-6 lg:p-8">
			<div className="max-w-7xl mx-auto space-y-6">
				{/* Header */}
				<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] border border-gray-100/50">
					<div>
						<h1 className="text-2xl font-bold text-gray-900 tracking-tight">电话分组管理</h1>
						<p className="mt-1 text-sm text-gray-500">管理电话号码分组，配置拥有人、比例及关联电话</p>
					</div>
					<button onClick={handleAdd} className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-600/20 transition-all duration-200">
						<svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
						</svg>
						新增分组
					</button>
				</div>

				{/* List */}
				<div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] border border-gray-100/50 overflow-hidden">
					{loading ? (
						<div className="p-8 text-center text-gray-500">加载中...</div>
					) : (
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50/50">
									<tr>
										<th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
										<th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">拥有人</th>
										<th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">比例</th>
										<th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">电话号码</th>
										<th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">操作</th>
									</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200">
									{groups.length === 0 ? (
										<tr>
											<td colSpan="5" className="px-6 py-8 text-center text-gray-500">暂无数据</td>
										</tr>
									) : (
										groups.map((group) => (
											<tr key={group.id} className="hover:bg-gray-50/50 transition-colors">
												<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{group.id}</td>
												<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{group.owner_name}</td>
												<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{group.ratio}</td>
												<td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={group.phone_numbers}>{group.phone_numbers}</td>
												<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
													<button onClick={() => handleEdit(group)} className="text-blue-600 hover:text-blue-900 mx-2 transition-colors">编辑</button>
													<button onClick={() => handleDelete(group.id)} className="text-red-600 hover:text-red-900 mx-2 transition-colors">删除</button>
												</td>
											</tr>
										))
									)}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>

			<Transition appear show={isModalOpen} as={React.Fragment}>
				<Dialog as="div" className="relative z-50" onClose={() => setIsModalOpen(false)}>
					<Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
						<div className="fixed inset-0 bg-gray-900/25 backdrop-blur-sm" />
					</Transition.Child>

					<div className="fixed inset-0 overflow-y-auto">
						<div className="flex min-h-full items-center justify-center p-4 text-center">
							<Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
								<Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
									<PhoneGroupForm onSubmit={handleSubmit} onCancel={() => setIsModalOpen(false)} initialValues={currentGroup || {}} />
								</Dialog.Panel>
							</Transition.Child>
						</div>
					</div>
				</Dialog>
			</Transition>
		</div>
	);
};

export default PhoneGroupList;